// packages/client/src/api/auth.ts
import "zod";

// packages/client/src/http/dispatcher.ts
import z from "zod";

// packages/client/src/http/interceptors.ts
var createHeadersInterceptor = (headers, requestHeaders) => {
  return {
    before({ init, url }) {
      for (const [key, value] of new Headers(requestHeaders)) {
        if (value !== void 0 && !init.headers.has(key)) {
          init.headers.set(key, value);
        }
      }
      for (const [key, value] of Object.entries(headers)) {
        if (value !== void 0 && !init.headers.has(key)) {
          init.headers.set(key, value);
        }
      }
      return { init, url };
    }
  };
};
var createBaseUrlInterceptor = (baseUrl) => {
  return {
    before({ init, url }) {
      if (url.protocol === "local:") {
        return {
          init,
          url: new URL(url.href.replace("local://", baseUrl))
        };
      }
      return { init, url };
    }
  };
};

// packages/client/src/http/request.ts
function template(templateString, templateVariables) {
  const nargs = /{([0-9a-zA-Z_]+)}/g;
  return templateString.replace(nargs, (match, key, index) => {
    if (templateString[index - 1] === "{" && templateString[index + match.length] === "}") {
      return key;
    }
    const result = key in templateVariables ? templateVariables[key] : null;
    return result === null || result === void 0 ? "" : String(result);
  });
}
function toWireValue(value) {
  return value instanceof Date ? value.toISOString() : String(value);
}
var Serializer = class {
  input;
  props;
  constructor(input, props) {
    this.input = input;
    this.props = props;
  }
  serialize(path) {
    const params = this.props.inputParams.reduce(
      (acc, key) => {
        acc[key] = this.input[key];
        return acc;
      },
      {}
    );
    const url = new URL(template(path, params), `local://`);
    const headers = { ...this.getHeaders() };
    for (const header of this.props.inputHeaders) {
      const value = this.input[header];
      if (value !== void 0) {
        headers[header] = toWireValue(value);
      }
    }
    for (const key of this.props.inputQuery) {
      const value = this.input[key];
      if (value !== void 0) {
        if (Array.isArray(value)) {
          for (const item of value) {
            url.searchParams.append(key, toWireValue(item));
          }
        } else {
          url.searchParams.set(key, toWireValue(value));
        }
      }
    }
    return {
      body: this.getBody(),
      url,
      headers
    };
  }
};
var JsonSerializer = class extends Serializer {
  getBody() {
    const body = {};
    if (this.props.inputBody.length === 1 && this.props.inputBody[0] === "$body") {
      return JSON.stringify(this.input.$body);
    }
    for (const prop of this.props.inputBody) {
      body[prop] = this.input[prop];
    }
    return JSON.stringify(body);
  }
  getHeaders() {
    return {
      "Content-Type": "application/json",
      Accept: "application/json"
    };
  }
};
var EmptySerializer = class extends Serializer {
  getBody() {
    return null;
  }
  getHeaders() {
    return {};
  }
};
function json(input, props) {
  return new JsonSerializer(input, props);
}
function empty(input, props) {
  return new EmptySerializer(input, props);
}
function toRequest(endpoint, serializer) {
  const [method, path] = endpoint.split(" ");
  const input = serializer.serialize(path);
  return {
    url: input.url,
    init: {
      method,
      headers: new Headers(input.headers),
      body: method === "GET" ? void 0 : input.body
    }
  };
}

// packages/client/src/http/parse-response.ts
import { parse } from "fast-content-type-parse";
function isBinaryContentType(contentType) {
  const type = contentType.toLowerCase();
  if (type.startsWith("image/")) {
    return true;
  }
  if (type.startsWith("audio/")) {
    return true;
  }
  if (type.startsWith("video/")) {
    return true;
  }
  switch (type) {
    case "application/pdf":
    case "application/zip":
    case "application/gzip":
    case "application/x-7z-compressed":
    case "application/x-tar":
    case "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
    case "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
    case "application/vnd.openxmlformats-officedocument.presentationml.presentation":
    case "application/vnd.ms-excel":
    case "application/vnd.ms-powerpoint":
    case "application/msword":
    case "application/octet-stream":
      return true;
    default:
      return false;
  }
}
function isAttachment(response) {
  const contentDisposition = response.headers.get("Content-Disposition");
  const dispositionType = contentDisposition?.split(";", 1)[0];
  return dispositionType?.trim().toLowerCase() === "attachment";
}
async function buffered(response) {
  if (response.status === 204 || response.status === 205 || response.status === 304) {
    return null;
  }
  if (isAttachment(response)) {
    return response.blob();
  }
  const contentType = response.headers.get("Content-Type");
  if (!contentType) {
    throw new Error("Content-Type header is missing");
  }
  const { type } = parse(contentType);
  if (isBinaryContentType(type)) {
    return response.blob();
  }
  if (type.startsWith("text/")) {
    return response.text();
  }
  switch (type) {
    case "application/json":
      return response.json();
    case "application/xml":
      return response.text();
    case "application/x-www-form-urlencoded": {
      const text = await response.text();
      return Object.fromEntries(new URLSearchParams(text));
    }
    case "multipart/form-data":
      return response.formData();
    default:
      throw new Error(`Unsupported content type: ${contentType}`);
  }
}

// packages/client/src/http/response.ts
var APIResponse = class {
  static status;
  status;
  data;
  headers;
  constructor(status, headers, data) {
    this.status = status;
    this.headers = headers;
    this.data = data;
  }
  static create(status, headers, data) {
    return new this(status, headers, data);
  }
};
var APIError = class extends APIResponse {
  static create(status, headers, data) {
    return new this(status, headers, data);
  }
};
var Ok = class _Ok extends APIResponse {
  static status = 200;
  constructor(headers, data) {
    super(_Ok.status, headers, data);
  }
  static create(status, headers, data) {
    return new this(headers, data);
  }
};
var Created = class _Created extends APIResponse {
  static status = 201;
  constructor(headers, data) {
    super(_Created.status, headers, data);
  }
  static create(status, headers, data) {
    return new this(headers, data);
  }
};
var Accepted = class _Accepted extends APIResponse {
  static status = 202;
  constructor(headers, data) {
    super(_Accepted.status, headers, data);
  }
  static create(status, headers, data) {
    return new this(headers, data);
  }
};
var NoContent = class _NoContent extends APIResponse {
  static status = 204;
  constructor(headers) {
    super(_NoContent.status, headers, null);
  }
  static create(status, headers, data) {
    return new this(headers);
  }
};
var BadRequest = class _BadRequest extends APIError {
  static status = 400;
  constructor(headers, data) {
    super(_BadRequest.status, headers, data);
  }
  static create(status, headers, data) {
    return new this(headers, data);
  }
};
var Unauthorized = class _Unauthorized extends APIError {
  static status = 401;
  constructor(headers, data) {
    super(_Unauthorized.status, headers, data);
  }
  static create(status, headers, data) {
    return new this(headers, data);
  }
};
var PaymentRequired = class _PaymentRequired extends APIError {
  static status = 402;
  constructor(headers, data) {
    super(_PaymentRequired.status, headers, data);
  }
  static create(status, headers, data) {
    return new this(headers, data);
  }
};
var Forbidden = class _Forbidden extends APIError {
  static status = 403;
  constructor(headers, data) {
    super(_Forbidden.status, headers, data);
  }
  static create(status, headers, data) {
    return new this(headers, data);
  }
};
var NotFound = class _NotFound extends APIError {
  static status = 404;
  constructor(headers, data) {
    super(_NotFound.status, headers, data);
  }
  static create(status, headers, data) {
    return new this(headers, data);
  }
};
var MethodNotAllowed = class _MethodNotAllowed extends APIError {
  static status = 405;
  constructor(headers, data) {
    super(_MethodNotAllowed.status, headers, data);
  }
  static create(status, headers, data) {
    return new this(headers, data);
  }
};
var NotAcceptable = class _NotAcceptable extends APIError {
  static status = 406;
  constructor(headers, data) {
    super(_NotAcceptable.status, headers, data);
  }
  static create(status, headers, data) {
    return new this(headers, data);
  }
};
var Conflict = class _Conflict extends APIError {
  static status = 409;
  constructor(headers, data) {
    super(_Conflict.status, headers, data);
  }
  static create(status, headers, data) {
    return new this(headers, data);
  }
};
var Gone = class _Gone extends APIError {
  static status = 410;
  constructor(headers, data) {
    super(_Gone.status, headers, data);
  }
  static create(status, headers, data) {
    return new this(headers, data);
  }
};
var PreconditionFailed = class _PreconditionFailed extends APIError {
  static status = 412;
  constructor(headers, data) {
    super(_PreconditionFailed.status, headers, data);
  }
  static create(status, headers, data) {
    return new this(headers, data);
  }
};
var UnprocessableEntity = class _UnprocessableEntity extends APIError {
  static status = 422;
  constructor(headers, data) {
    super(_UnprocessableEntity.status, headers, data);
  }
  static create(status, headers, data) {
    return new this(headers, data);
  }
};
var TooManyRequests = class _TooManyRequests extends APIError {
  static status = 429;
  constructor(headers, data) {
    super(_TooManyRequests.status, headers, data);
  }
  static create(status, headers, data) {
    return new this(headers, data);
  }
};
var PayloadTooLarge = class _PayloadTooLarge extends APIError {
  static status = 413;
  constructor(headers, data) {
    super(_PayloadTooLarge.status, headers, data);
  }
  static create(status, headers, data) {
    return new this(headers, data);
  }
};
var UnsupportedMediaType = class _UnsupportedMediaType extends APIError {
  static status = 415;
  constructor(headers, data) {
    super(_UnsupportedMediaType.status, headers, data);
  }
  static create(status, headers, data) {
    return new this(headers, data);
  }
};
var InternalServerError = class _InternalServerError extends APIError {
  static status = 500;
  constructor(headers, data) {
    super(_InternalServerError.status, headers, data);
  }
  static create(status, headers, data) {
    return new this(headers, data);
  }
};
var NotImplemented = class _NotImplemented extends APIError {
  static status = 501;
  constructor(headers, data) {
    super(_NotImplemented.status, headers, data);
  }
  static create(status, headers, data) {
    return new this(headers, data);
  }
};
var BadGateway = class _BadGateway extends APIError {
  static status = 502;
  constructor(headers, data) {
    super(_BadGateway.status, headers, data);
  }
  static create(status, headers, data) {
    return new this(headers, data);
  }
};
var ServiceUnavailable = class _ServiceUnavailable extends APIError {
  static status = 503;
  constructor(headers, data) {
    super(_ServiceUnavailable.status, headers, data);
  }
  static create(status, headers, data) {
    return new this(headers, data);
  }
};
var GatewayTimeout = class _GatewayTimeout extends APIError {
  static status = 504;
  constructor(headers, data) {
    super(_GatewayTimeout.status, headers, data);
  }
  static create(status, headers, data) {
    return new this(headers, data);
  }
};

// packages/client/src/http/dispatcher.ts
var fetchType = z.function({
  input: [z.custom()],
  output: z.custom()
}).optional();
async function parse2(outputs7, response, mapper) {
  let output = null;
  let parser = buffered;
  for (const outputType of outputs7) {
    if ("parser" in outputType) {
      if (isTypeOf(outputType.type, APIResponse)) {
        if (response.status === outputType.type.status) {
          parser = outputType.parser;
          output = outputType.type;
          break;
        }
      }
    } else if (isTypeOf(outputType, APIResponse)) {
      if (response.status === outputType.status) {
        output = outputType;
        break;
      }
    }
  }
  if (response.ok) {
    const data = await parser(response);
    const mapped = mapper ? mapper(data) : data;
    const apiresponse = (output || APIResponse).create(
      response.status,
      response.headers,
      mapped
    );
    return apiresponse;
  }
  throw (output || APIError).create(
    response.status,
    response.headers,
    await parser(response)
  );
}
function isTypeOf(instance, baseType) {
  if (instance === baseType) {
    return true;
  }
  const prototype = Object.getPrototypeOf(instance);
  if (prototype === null) {
    return false;
  }
  return isTypeOf(prototype, baseType);
}
var Dispatcher = class {
  #interceptors = [];
  #fetch;
  constructor(interceptors, fetch2) {
    this.#interceptors = interceptors;
    this.#fetch = fetch2;
  }
  async send(config, outputs7, signal, mapper) {
    for (const interceptor of this.#interceptors) {
      if (interceptor.before) {
        config = await interceptor.before(config);
      }
    }
    const init = signal === void 0 ? config.init : { ...config.init, signal };
    let response = await (this.#fetch ?? fetch)(new Request(config.url, init));
    for (let i = this.#interceptors.length - 1; i >= 0; i--) {
      const interceptor = this.#interceptors[i];
      if (interceptor.after) {
        response = await interceptor.after(response.clone());
      }
    }
    if (mapper) {
      return await parse2(outputs7, response, mapper);
    }
    return await parse2(outputs7, response);
  }
};

// packages/client/src/inputs/auth.ts
import { z as z2 } from "zod";
var changeOwnPasswordSchema = z2.object({ newPassword: z2.string() });
var firebaseAuthActionSchema = z2.object({
  apiKey: z2.string(),
  lang: z2.string().optional(),
  mode: z2.enum(["resetPassword", "verifyEmail"]),
  oobCode: z2.string()
});
var forgotPasswordSchema = z2.object({ email: z2.email() });
var sendVerificationEmailSchema = z2.object({}).catchall(z2.unknown());
var passwordChangedSchema = z2.object({ email: z2.email() });
var getAdminMeSchema = z2.object({}).catchall(z2.unknown());

// packages/client/src/api/auth.ts
var auth_default = {
  "POST /me/password": {
    schema: changeOwnPasswordSchema,
    output: [
      NoContent,
      BadRequest,
      Unauthorized,
      Conflict,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "POST /me/password",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["newPassword"],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /auth/action": {
    schema: firebaseAuthActionSchema,
    output: [
      Ok,
      APIResponse,
      BadRequest,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /auth/action",
        empty(input, {
          inputHeaders: [],
          inputQuery: ["apiKey", "lang", "mode", "oobCode"],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "POST /auth/forgot-password": {
    schema: forgotPasswordSchema,
    output: [
      Ok,
      BadRequest,
      Conflict,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "POST /auth/forgot-password",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["email"],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "POST /auth/send-verification": {
    schema: sendVerificationEmailSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      NotFound,
      Conflict,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "POST /auth/send-verification",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "POST /auth/password-changed": {
    schema: passwordChangedSchema,
    output: [
      Ok,
      BadRequest,
      Conflict,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "POST /auth/password-changed",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["email"],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /admin/me": {
    schema: getAdminMeSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /admin/me",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  }
};

// packages/client/src/api/courses.ts
import "zod";

// packages/client/src/inputs/courses.ts
import { z as z4 } from "zod";
var listCoursesSchema = z4.object({
  page: z4.coerce.number().int().gt(0).optional().default(1),
  pageSize: z4.coerce.number().int().min(1).max(1e3).optional().default(50),
  search: z4.union([z4.string(), z4.string(), z4.string()]).optional(),
  sortBy: z4.enum(["title", "price", "createdAt"]).default("createdAt"),
  sortOrder: z4.enum(["asc", "desc"]).optional().default("desc")
});
var getCourseSchema = z4.object({ slug: z4.string() });
var listMyCoursesSchema = z4.object({
  page: z4.coerce.number().int().gt(0).optional().default(1),
  pageSize: z4.coerce.number().int().min(1).max(1e3).optional().default(50)
});
var getMyCourseSchema = z4.object({ slug: z4.string() });
var listAdminCoursesSchema = z4.object({
  page: z4.coerce.number().int().gt(0).optional().default(1),
  pageSize: z4.coerce.number().int().min(1).max(1e3).optional().default(50),
  search: z4.union([z4.string(), z4.string(), z4.string()]).optional(),
  sortBy: z4.enum(["title", "price", "createdAt"]).default("createdAt"),
  sortOrder: z4.enum(["asc", "desc"]).optional().default("desc")
});
var createCourseSchema = z4.object({
  category: z4.string().nullable().optional(),
  comingSoon: z4.boolean().default(false),
  description: z4.string(),
  durationHours: z4.number().nullable().optional(),
  endDate: z4.string().nullable().optional(),
  featured: z4.boolean().default(false),
  imageUrl: z4.string(),
  instructorIds: z4.array(z4.string()).optional(),
  languages: z4.array(z4.string()).optional(),
  level: z4.enum(["INTRODUCTORY", "INTERMEDIATE", "PROFESSIONAL"]).default("INTRODUCTORY"),
  location: z4.string().nullable().optional(),
  objectives: z4.array(z4.string()).optional(),
  oneLiner: z4.string().nullable().optional(),
  prerequisites: z4.string().nullable().optional(),
  price: z4.number().nullable().optional(),
  published: z4.boolean().default(false),
  showModules: z4.boolean().default(false),
  startDate: z4.string().nullable().optional(),
  targetedAreas: z4.array(z4.string()).optional(),
  targetedAudience: z4.string().nullable().optional(),
  title: z4.string(),
  trainingMethod: z4.string().nullable().optional()
});
var getAdminCourseSchema = z4.object({ id: z4.string() });
var updateCourseSchema = z4.object({
  category: z4.string().nullable().optional(),
  comingSoon: z4.boolean().optional(),
  description: z4.string().optional(),
  durationHours: z4.number().nullable().optional(),
  endDate: z4.string().nullable().optional(),
  featured: z4.boolean().optional(),
  imageUrl: z4.string().optional(),
  instructorIds: z4.array(z4.string()).optional(),
  languages: z4.array(z4.string()).optional(),
  level: z4.enum(["INTRODUCTORY", "INTERMEDIATE", "PROFESSIONAL"]).optional(),
  location: z4.string().nullable().optional(),
  objectives: z4.array(z4.string()).optional(),
  oneLiner: z4.string().nullable().optional(),
  prerequisites: z4.string().nullable().optional(),
  price: z4.number().nullable().optional(),
  published: z4.boolean().optional(),
  showModules: z4.boolean().optional(),
  startDate: z4.string().nullable().optional(),
  targetedAreas: z4.array(z4.string()).optional(),
  targetedAudience: z4.string().nullable().optional(),
  title: z4.string().optional(),
  trainingMethod: z4.string().nullable().optional(),
  id: z4.string()
});
var deleteCourseSchema = z4.object({ id: z4.string() });

// packages/client/src/api/courses.ts
var courses_default = {
  "GET /courses": {
    schema: listCoursesSchema,
    output: [
      Ok,
      BadRequest,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /courses",
        empty(input, {
          inputHeaders: [],
          inputQuery: ["page", "pageSize", "search", "sortBy", "sortOrder"],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /courses/{slug}": {
    schema: getCourseSchema,
    output: [
      Ok,
      BadRequest,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /courses/{slug}",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["slug"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /me/courses": {
    schema: listMyCoursesSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /me/courses",
        empty(input, {
          inputHeaders: [],
          inputQuery: ["page", "pageSize"],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /me/courses/{slug}": {
    schema: getMyCourseSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /me/courses/{slug}",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["slug"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /admin/courses": {
    schema: listAdminCoursesSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /admin/courses",
        empty(input, {
          inputHeaders: [],
          inputQuery: ["page", "pageSize", "search", "sortBy", "sortOrder"],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "POST /admin/courses": {
    schema: createCourseSchema,
    output: [
      Created,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "POST /admin/courses",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [
            "category",
            "comingSoon",
            "description",
            "durationHours",
            "endDate",
            "featured",
            "imageUrl",
            "instructorIds",
            "languages",
            "level",
            "location",
            "objectives",
            "oneLiner",
            "prerequisites",
            "price",
            "published",
            "showModules",
            "startDate",
            "targetedAreas",
            "targetedAudience",
            "title",
            "trainingMethod"
          ],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /admin/courses/{id}": {
    schema: getAdminCourseSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /admin/courses/{id}",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "PATCH /admin/courses/{id}": {
    schema: updateCourseSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "PATCH /admin/courses/{id}",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [
            "category",
            "comingSoon",
            "description",
            "durationHours",
            "endDate",
            "featured",
            "imageUrl",
            "instructorIds",
            "languages",
            "level",
            "location",
            "objectives",
            "oneLiner",
            "prerequisites",
            "price",
            "published",
            "showModules",
            "startDate",
            "targetedAreas",
            "targetedAudience",
            "title",
            "trainingMethod"
          ],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "DELETE /admin/courses/{id}": {
    schema: deleteCourseSchema,
    output: [
      NoContent,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "DELETE /admin/courses/{id}",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  }
};

// packages/client/src/api/files.ts
import "zod";

// packages/client/src/inputs/files.ts
import { z as z6 } from "zod";
var getSignedUrlSchema = z6.object({
  name: z6.string(),
  public: z6.boolean().default(false),
  size: z6.number().int().gt(0),
  type: z6.enum(["image/png", "image/jpeg", "image/jpg", "image/webp"])
});
var getPublicFileSchema = z6.object({ id: z6.string() });
var markFileAsUsedSchema = z6.object({ id: z6.string() });
var getFilesSchema = z6.object({ userId: z6.string().optional() });
var getFileByIdSchema = z6.object({ id: z6.string() });
var getFileAccessSchema = z6.object({
  id: z6.string(),
  purpose: z6.enum(["view", "download"])
});

// packages/client/src/api/files.ts
var files_default = {
  "POST /signed-url": {
    schema: getSignedUrlSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      PayloadTooLarge,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "POST /signed-url",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["name", "public", "size", "type"],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /files/{id}/raw": {
    schema: getPublicFileSchema,
    output: [
      Ok,
      APIResponse,
      BadRequest,
      NotFound,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /files/{id}/raw",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "POST /files/{id}/used": {
    schema: markFileAsUsedSchema,
    output: [
      Created,
      BadRequest,
      Unauthorized,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "POST /files/{id}/used",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /files": {
    schema: getFilesSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /files",
        empty(input, {
          inputHeaders: [],
          inputQuery: ["userId"],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /files/{id}": {
    schema: getFileByIdSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /files/{id}",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /files/{id}/access": {
    schema: getFileAccessSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /files/{id}/access",
        empty(input, {
          inputHeaders: [],
          inputQuery: ["purpose"],
          inputBody: [],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  }
};

// packages/client/src/api/instructors.ts
import "zod";

// packages/client/src/inputs/instructors.ts
import { z as z8 } from "zod";
var listInstructorsSchema = z8.object({
  page: z8.coerce.number().int().gt(0).optional().default(1),
  pageSize: z8.coerce.number().int().min(1).max(1e3).optional().default(50),
  search: z8.union([z8.string(), z8.string(), z8.string()]).optional()
});
var getInstructorSchema = z8.object({ slug: z8.string() });
var listAdminInstructorsSchema = z8.object({
  page: z8.coerce.number().int().gt(0).optional().default(1),
  pageSize: z8.coerce.number().int().min(1).max(1e3).optional().default(50),
  search: z8.union([z8.string(), z8.string(), z8.string()]).optional()
});
var createInstructorSchema = z8.object({
  bio: z8.string(),
  imageUrl: z8.string().nullable().optional(),
  institution: z8.string(),
  name: z8.string(),
  position: z8.string()
});
var updateInstructorSchema = z8.object({
  bio: z8.string().optional(),
  imageUrl: z8.string().nullable().optional(),
  institution: z8.string().optional(),
  name: z8.string().optional(),
  position: z8.string().optional(),
  id: z8.string()
});
var deleteInstructorSchema = z8.object({ id: z8.string() });

// packages/client/src/api/instructors.ts
var instructors_default = {
  "GET /instructors": {
    schema: listInstructorsSchema,
    output: [
      Ok,
      BadRequest,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /instructors",
        empty(input, {
          inputHeaders: [],
          inputQuery: ["page", "pageSize", "search"],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /instructors/{slug}": {
    schema: getInstructorSchema,
    output: [
      Ok,
      BadRequest,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /instructors/{slug}",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["slug"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /admin/instructors": {
    schema: listAdminInstructorsSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /admin/instructors",
        empty(input, {
          inputHeaders: [],
          inputQuery: ["page", "pageSize", "search"],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "POST /admin/instructors": {
    schema: createInstructorSchema,
    output: [
      Created,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "POST /admin/instructors",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["bio", "imageUrl", "institution", "name", "position"],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "PATCH /admin/instructors/{id}": {
    schema: updateInstructorSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      Forbidden,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "PATCH /admin/instructors/{id}",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["bio", "imageUrl", "institution", "name", "position"],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "DELETE /admin/instructors/{id}": {
    schema: deleteInstructorSchema,
    output: [
      NoContent,
      BadRequest,
      Unauthorized,
      Forbidden,
      Conflict,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "DELETE /admin/instructors/{id}",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: ["id"]
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  }
};

// packages/client/src/api/me.ts
import "zod";

// packages/client/src/inputs/me.ts
import { z as z10 } from "zod";
var registerLearnerSchema = z10.object({ name: z10.string() });
var getMeSchema = z10.object({}).catchall(z10.unknown());

// packages/client/src/api/me.ts
var me_default = {
  "POST /me/register": {
    schema: registerLearnerSchema,
    output: [
      Created,
      BadRequest,
      Unauthorized,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "POST /me/register",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["name"],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "GET /me": {
    schema: getMeSchema,
    output: [
      Ok,
      BadRequest,
      Unauthorized,
      UnsupportedMediaType
    ],
    toRequest(input) {
      return toRequest(
        "GET /me",
        empty(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  }
};

// packages/client/src/api/payments.ts
import "zod";

// packages/client/src/inputs/payments.ts
import { z as z12 } from "zod";
var moyasarCallbackSchema = z12.object({ id: z12.string() });
var moyasarWebhookSchema = z12.object({
  data: z12.object({ id: z12.string() }),
  live: z12.boolean(),
  secret_token: z12.string(),
  type: z12.string()
});

// packages/client/src/api/payments.ts
var payments_default = {
  "GET /payments/callback": {
    schema: moyasarCallbackSchema,
    output: [
      Ok,
      BadRequest,
      Conflict,
      UnsupportedMediaType,
      BadGateway
    ],
    toRequest(input) {
      return toRequest(
        "GET /payments/callback",
        empty(input, {
          inputHeaders: [],
          inputQuery: ["id"],
          inputBody: [],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  },
  "POST /payments/moyasar/webhook": {
    schema: moyasarWebhookSchema,
    output: [
      Ok,
      BadRequest,
      Forbidden,
      Conflict,
      UnsupportedMediaType,
      BadGateway
    ],
    toRequest(input) {
      return toRequest(
        "POST /payments/moyasar/webhook",
        json(input, {
          inputHeaders: [],
          inputQuery: [],
          inputBody: ["data", "live", "secret_token", "type"],
          inputParams: []
        })
      );
    },
    async dispatch(input, options) {
      const dispatcher = new Dispatcher(options.interceptors, options.fetch);
      return dispatcher.send(
        this.toRequest(input),
        this.output,
        options?.signal
      );
    }
  }
};

// packages/client/src/api/schemas.ts
var schemas_default = {
  ...auth_default,
  ...courses_default,
  ...instructors_default,
  ...me_default,
  ...payments_default,
  ...files_default
};

// packages/client/src/client.ts
import z15 from "zod";

// packages/client/src/http/parser.ts
import { z as z14 } from "zod";
var ParseError = class extends Error {
  data;
  constructor(data) {
    super("Validation failed");
    this.name = "ParseError";
    this.data = data;
  }
};
function parseInput(schema, input) {
  const result = schema.safeParse(input);
  if (!result.success) {
    const error = z14.flattenError(result.error, (issue) => issue);
    throw new ParseError(error);
  }
  return result.data;
}

// packages/client/src/client.ts
var optionsSchema = z15.object({
  token: z15.union([
    z15.string(),
    z15.custom(
      (value) => typeof value === "function"
    )
  ]).optional().transform(async (token, ctx) => {
    if (!token) return void 0;
    const value = typeof token === "function" ? await token() : token;
    if (typeof value !== "string") {
      ctx.addIssue({
        code: "custom",
        message: "token must resolve to a string"
      });
      return z15.NEVER;
    }
    return `Bearer ${value}`;
  }).describe(
    "Bearer token for authentication. Can be a string or a function that returns a string."
  ),
  fetch: fetchType.describe(
    "Custom fetch implementation. Defaults to globalThis.fetch."
  ),
  baseUrl: z15.union([
    z15.string(),
    z15.custom(
      (value) => typeof value === "function"
    )
  ]).transform(async (baseUrl, ctx) => {
    const value = typeof baseUrl === "function" ? await baseUrl() : baseUrl;
    if (typeof value !== "string") {
      ctx.addIssue({
        code: "custom",
        message: "baseUrl must resolve to a string"
      });
      return z15.NEVER;
    }
    return value;
  }).describe(
    "Base URL of the API server. Can be a string or a function that returns a string."
  ),
  headers: z15.record(z15.string(), z15.string()).optional().describe("Default headers to include in all requests."),
  skipValidation: z15.boolean().optional().describe(
    "Skip request input validation. Client options and TypeScript types still enforce correct usage."
  )
});
var Education = class {
  options;
  constructor(options) {
    this.options = options;
  }
  /** Sends a request and returns the unwrapped response data. Delegates to the standalone {@link request} function. */
  async request(endpoint, input, options) {
    return request(this, endpoint, input, options).then(function unwrap(it) {
      if (it instanceof APIResponse) {
        return it.data;
      }
      return it;
    });
  }
  /** Builds a ready-to-send request without sending it. Delegates to the standalone {@link prepare} function. */
  async prepare(endpoint, input, options) {
    return prepare(this, endpoint, input, options);
  }
  async defaultHeaders() {
    const options = await optionsSchema.parseAsync(this.options);
    return {
      ...{ authorization: options["token"] },
      ...options.headers
    };
  }
  async defaultInputs() {
    const options = await optionsSchema.parseAsync(this.options);
    return {};
  }
  setOptions(options) {
    this.options = {
      ...this.options,
      ...options
    };
  }
};
async function request(client, endpoint, input, requestOptions) {
  const route = schemas_default[endpoint];
  const options = await optionsSchema.parseAsync(client.options);
  const withDefaultInputs = Object.assign({}, {}, input);
  const parsedInput = options.skipValidation ? withDefaultInputs : parseInput(route.schema, withDefaultInputs);
  const result = await route.dispatch(parsedInput, {
    fetch: options.fetch,
    interceptors: [
      createHeadersInterceptor(
        { ...{ authorization: options["token"] }, ...options.headers },
        requestOptions?.headers ?? {}
      ),
      createBaseUrlInterceptor(options.baseUrl)
    ],
    signal: requestOptions?.signal
  });
  return result;
}
async function prepare(client, endpoint, input, requestOptions) {
  const route = schemas_default[endpoint];
  const options = await optionsSchema.parseAsync(client.options);
  const withDefaultInputs = Object.assign({}, {}, input);
  const parsedInput = options.skipValidation ? withDefaultInputs : parseInput(route.schema, withDefaultInputs);
  const interceptors = [
    createHeadersInterceptor(
      { ...{ authorization: options["token"] }, ...options.headers },
      requestOptions?.headers ?? {}
    ),
    createBaseUrlInterceptor(options.baseUrl)
  ];
  let config = route.toRequest(parsedInput);
  if (requestOptions?.signal) {
    config = {
      ...config,
      init: {
        ...config.init,
        signal: requestOptions.signal
      }
    };
  }
  for (const interceptor of interceptors) {
    if (interceptor.before) {
      config = await interceptor.before(config);
    }
  }
  return {
    ...config,
    parse: (response) => parse2(route.output, response, (d) => d)
  };
}
export {
  APIError,
  APIResponse,
  Accepted,
  BadGateway,
  BadRequest,
  Conflict,
  Created,
  Education,
  Forbidden,
  GatewayTimeout,
  Gone,
  InternalServerError,
  MethodNotAllowed,
  NoContent,
  NotAcceptable,
  NotFound,
  NotImplemented,
  Ok,
  ParseError,
  PayloadTooLarge,
  PaymentRequired,
  PreconditionFailed,
  ServiceUnavailable,
  TooManyRequests,
  Unauthorized,
  UnprocessableEntity,
  UnsupportedMediaType,
  changeOwnPasswordSchema,
  createCourseSchema,
  createInstructorSchema,
  deleteCourseSchema,
  deleteInstructorSchema,
  firebaseAuthActionSchema,
  forgotPasswordSchema,
  getAdminCourseSchema,
  getAdminMeSchema,
  getCourseSchema,
  getFileAccessSchema,
  getFileByIdSchema,
  getFilesSchema,
  getInstructorSchema,
  getMeSchema,
  getMyCourseSchema,
  getPublicFileSchema,
  getSignedUrlSchema,
  listAdminCoursesSchema,
  listAdminInstructorsSchema,
  listCoursesSchema,
  listInstructorsSchema,
  listMyCoursesSchema,
  markFileAsUsedSchema,
  moyasarCallbackSchema,
  moyasarWebhookSchema,
  parseInput,
  passwordChangedSchema,
  prepare,
  registerLearnerSchema,
  request,
  sendVerificationEmailSchema,
  updateCourseSchema,
  updateInstructorSchema
};
//# sourceMappingURL=index.js.map
